import { findSettingsItem } from "@/components/settings/settings-nav-data.ts";
import { useSettingsNav } from "@/components/settings/settings-sidebar.tsx";
import { PageEnter } from "@/components/custom/page-enter.tsx";
import { m } from "@/paraglide/messages.js";
import { SettingsPane } from "@/components/custom/settings-pane.tsx";
import { SettingsPaneSearchProvider } from "@/components/custom/settings-pane-search.tsx";
import {
	createFileRoute,
	Outlet,
	useLocation,
	useMatches,
} from "@tanstack/react-router";
import { pageTitle } from "@/utils/page-title.ts";

declare module "@tanstack/react-router" {
	interface StaticDataRouteOption {
		/**
		 * `full`: the page owns the whole settings pane - its own header, its own
		 * columns, its own scroll container - instead of sitting in the form
		 * column under the nav-derived title. For the page with a rail beside
		 * its content.
		 */
		settingsLayout?: "full";
		/**
		 * The reading width this page wants inside the shared shell. Omitted is
		 * the form column every settings page gets; `wide` is the opt-out for a
		 * page whose content is a grid or a table and reads badly squeezed into
		 * it (Rights & Roles). A page that owns the whole pane declares
		 * `settingsLayout: "full"` instead - that is a different thing.
		 */
		settingsWidth?: "wide";
	}
}

export const Route = createFileRoute("/_authenticated/settings")({
	component: SettingsLayout,
	head: () => ({
		meta: [{ title: pageTitle(m.profile_tabs_settings()) }],
	}),
});

/**
 * Settings is a normal authenticated page: the app sidebar (shared with the
 * dashboard) swaps to the settings navigation on these routes, so the layout
 * here only owns the scrolling content and its page title. Navigation back up
 * is the sidebar's job — these pages carry no breadcrumb.
 */
function SettingsLayout() {
	const location = useLocation();
	const groups = useSettingsNav();
	const activeItem = findSettingsItem(groups, location.pathname);
	const matches = useMatches();
	const sectionKey = matches.at(-1)?.routeId ?? "";
	const fullPane = matches.some(
		(match) => match.staticData.settingsLayout === "full",
	);
	const wide = matches.some(
		(match) => match.staticData.settingsWidth === "wide",
	);

	if (fullPane) {
		return <Outlet />;
	}

	// Every other settings page gets the same shell the full-pane pages built:
	// the pane header carrying the page's nav mark and name, then the page's own
	// scroller with the centred 850px reading column (SettingsPane's default
	// width - identical to Models). `data-scroll-reset` inside SettingsPane
	// resets the scroller on forward navigation (see `scrollToTopSelectors` in
	// main.tsx).
	// The page's own search, drawn in the header rather than in its body - see
	// `settings-pane-search.tsx` for why it travels through a context instead of
	// a prop.
	return (
		<SettingsPaneSearchProvider>
			{(search) => (
				<SettingsPane
					icon={activeItem?.icon}
					title={activeItem?.title ?? m.profile_tabs_settings()}
					width={wide ? "wide" : "column"}
					search={search ?? undefined}
				>
					{/* Each section flips in as the user moves through the settings
					    nav, while the pane header holds still. The wrapper repeats
					    the pane's own column rhythm: the pane's gap only sees this
					    one element, so a page that stacks several blocks would
					    otherwise have them sit flush. */}
					<PageEnter pageKey={sectionKey} className="flex flex-col gap-8">
						<Outlet />
					</PageEnter>
				</SettingsPane>
			)}
		</SettingsPaneSearchProvider>
	);
}
